// External
import { useMemo } from "react";
import _ from "lodash";

export const useTranscriptData = ({
  academicRecordsData,
  orgGradeScalesData,
  curriculumProgramsData,
  selectedGradeId,
  draftConfiguration,
  effectiveGradingPeriods: effectiveGradingPeriodsProp,
  t,
}) => {
  const { gradeMapById, schoolNameByProgramId } = useMemo(
    () =>
      _.reduce(
        _.get(academicRecordsData, "node.transcriptSummary.grades", []),
        (acc, gradeMap) => {
          const id = String(_.get(gradeMap, "id", ""));
          const programId = _.get(gradeMap, "school.sourceId");
          const schoolName = _.get(gradeMap, "school.name");

          if (id) {
            acc.gradeMapById[id] = gradeMap;
          }

          if (
            programId &&
            schoolName &&
            !acc.schoolNameByProgramId[programId]
          ) {
            acc.schoolNameByProgramId[programId] = schoolName;
          }

          return acc;
        },
        {
          gradeMapById: {},
          schoolNameByProgramId: {},
        }
      ),
    [academicRecordsData]
  );

  const selectedGradeMap = useMemo(() => {
    if (!selectedGradeId) {
      return null;
    }

    return _.get(gradeMapById, String(selectedGradeId), null);
  }, [gradeMapById, selectedGradeId]);

  const selectedGradeConfiguration = useMemo(
    () => _.get(selectedGradeMap, "configuration", null),
    [selectedGradeMap]
  );

  const originalConfiguration = useMemo(
    () =>
      selectedGradeConfiguration
        ? selectedGradeConfiguration
        : _.get(
            academicRecordsData,
            "node.transcriptSummary.configuration",
            {}
          ),
    [academicRecordsData, selectedGradeConfiguration]
  );

  const transcriptConfiguration = useMemo(() => {
    if (!draftConfiguration) {
      return originalConfiguration;
    }

    return {
      ...originalConfiguration,
      ...draftConfiguration,
    };
  }, [draftConfiguration, originalConfiguration]);

  const programIdByGradeScaleId = useMemo(
    () =>
      _.reduce(
        curriculumProgramsData,
        (acc, program) => {
          const programId = _.get(program, "id");
          _.forEach(_.get(program, "gradeScale.edges", []), edge => {
            const gradeScaleId = _.get(edge, "node.id");
            if (programId && gradeScaleId && !acc[gradeScaleId]) {
              acc[gradeScaleId] = programId;
            }
          });
          return acc;
        },
        {}
      ),
    [curriculumProgramsData]
  );

  const { transcriptToSourceIdMap, manageColumnItemIdSet } = useMemo(
    () =>
      _.reduce(
        orgGradeScalesData,
        (acc, gradeScale) => {
          const transcriptId = _.get(gradeScale, "id");
          const sourceId = _.get(gradeScale, "source.id");
          const isCustom = _.get(gradeScale, "isCustom", false);

          if (!isCustom && transcriptId && sourceId) {
            acc.transcriptToSourceIdMap[transcriptId] = sourceId;
          }

          if (transcriptId) {
            acc.manageColumnItemIdSet.add(transcriptId);
          }

          if (!isCustom && sourceId) {
            acc.manageColumnItemIdSet.add(sourceId);
          }

          return acc;
        },
        {
          transcriptToSourceIdMap: {},
          manageColumnItemIdSet: new Set(),
        }
      ),
    [orgGradeScalesData]
  );

  const gradeScaleDescriptionMap = useMemo(
    () =>
      _.reduce(
        orgGradeScalesData,
        (acc, gradeScale) => {
          const transcriptId = _.get(gradeScale, "id");
          const sourceId = _.get(gradeScale, "source.id");
          const isCustom = _.get(gradeScale, "isCustom", false);

          if (selectedGradeId || isCustom) {
            return acc;
          }

          const programId = _.get(programIdByGradeScaleId, sourceId);
          const schoolName = _.get(schoolNameByProgramId, programId);
          if (!schoolName) {
            return acc;
          }

          const description = t(
            "transcript:applicable_for_all_grade_levels_of",
            {
              schoolName,
            }
          );

          if (transcriptId) {
            acc[transcriptId] = description;
          }

          if (sourceId) {
            acc[sourceId] = description;
          }

          return acc;
        },
        {}
      ),
    [
      orgGradeScalesData,
      programIdByGradeScaleId,
      schoolNameByProgramId,
      selectedGradeId,
      t,
    ]
  );

  const gradingPeriods = useMemo(
    () =>
      !_.isNil(effectiveGradingPeriodsProp)
        ? effectiveGradingPeriodsProp
        : _.get(selectedGradeMap, "gradingPeriods", []),
    [effectiveGradingPeriodsProp, selectedGradeMap]
  );

  return {
    gradeMapById,
    schoolNameByProgramId,
    selectedGradeMap,
    selectedGradeConfiguration,
    originalConfiguration,
    transcriptConfiguration,
    programIdByGradeScaleId,
    gradeScaleDescriptionMap,
    transcriptToSourceIdMap,
    manageColumnItemIdSet,
    gradingPeriods,
  };
};
